import React from "react";
import { motion } from "framer-motion";
import { Rise } from "./Reveal";
import { useLang } from "../../lib/LangContext";
import { GALLERY } from "../../lib/products";

// Editorial masonry gallery of AI-photographed products.
// Layout: 12-col grid with tiles of varying aspect ratios (tall / square / wide)
// so the composition breaks the standard grid feel.
const SPANS = [
  { c: "col-span-12 md:col-span-6 aspect-[4/3]" },     // 0 wide
  { c: "col-span-6 md:col-span-3 aspect-[3/4]" },      // 1 tall
  { c: "col-span-6 md:col-span-3 aspect-[3/4]" },      // 2 tall
  { c: "col-span-6 md:col-span-4 aspect-square" },     // 3 sq
  { c: "col-span-6 md:col-span-4 aspect-[3/4]" },      // 4 tall
  { c: "col-span-12 md:col-span-4 aspect-square" },    // 5 sq
  { c: "col-span-12 md:col-span-8 aspect-[16/10]" },   // 6 wide
  { c: "col-span-6 md:col-span-4 aspect-[3/4]" },      // 7 tall
  { c: "col-span-6 md:col-span-4 aspect-square" },     // 8
  { c: "col-span-6 md:col-span-4 aspect-square" },     // 9
  { c: "col-span-6 md:col-span-4 aspect-[3/4]" },      // 10
  { c: "col-span-6 md:col-span-3 aspect-square" },     // 11
  { c: "col-span-6 md:col-span-3 aspect-square" },     // 12
  { c: "col-span-6 md:col-span-3 aspect-[3/4]" },      // 13
  { c: "col-span-6 md:col-span-3 aspect-[3/4]" },      // 14
];

export default function ProductsGallery() {
  const { dict, isRTL } = useLang();

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#F7F5F0] border-t hairline py-24 md:py-32"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20 items-end">
          <div className="col-span-12 md:col-span-3">
            <span className="label text-[#1A1918]">{dict.gallery_kicker}</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.02em] max-w-[22ch] text-[#1A1918]">
              {dict.gallery_title_1}
              <br />
              <span className="italic text-[#5C5A55]">{dict.gallery_title_2}</span>
            </h2>
            <p className="mt-8 text-[15px] md:text-[16px] leading-[1.7] text-[#5C5A55] max-w-[52ch]">
              {dict.gallery_body}
            </p>
            <a
              href="#catalogue"
              data-testid="gallery-view-catalogue"
              className="mt-10 inline-flex items-center gap-3 px-5 py-3 border border-[#1A1918] text-[#1A1918] label text-[10px] hover:bg-[#1A1918] hover:text-[#F7F5F0] transition-colors duration-500"
            >
              {dict.gallery_view_catalogue}
              <span aria-hidden>{isRTL ? "←" : "→"}</span>
            </a>
          </div>
        </div>

        {/* Masonry grid */}
        <div className="grid grid-cols-12 gap-3 md:gap-5">
          {GALLERY.map((tile, i) => {
            const span = SPANS[i % SPANS.length].c;
            return (
              <Rise
                key={tile.key}
                delay={(i % 3) * 0.06}
                y={22}
                className={span}
              >
                <a
                  href="#catalogue"
                  data-testid={`gallery-tile-${tile.key}`}
                  className="group relative block w-full h-full overflow-hidden bg-[#EAE6DF]"
                >
                  <motion.img
                    src={tile.src}
                    alt={tile.caption}
                    loading="lazy"
                    initial={{ scale: 1.02 }}
                    whileHover={{ scale: 1.06 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover spot"
                  />
                  {/* Ref tag */}
                  <div className="absolute top-3 left-3 label text-[9px] text-[#F7F5F0] mix-blend-difference">
                    {tile.ref}
                  </div>
                  {/* Caption strip on hover */}
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 flex items-end justify-between">
                    <div className="bg-[#1A1918]/0 group-hover:bg-[#1A1918]/90 transition-colors duration-500 px-3 py-2">
                      <div className="serif text-sm md:text-base text-[#F7F5F0] opacity-0 group-hover:opacity-100 transition-opacity duration-500 whitespace-nowrap">
                        {tile.caption}
                      </div>
                    </div>
                  </div>
                </a>
              </Rise>
            );
          })}
        </div>
      </div>
    </section>
  );
}
